import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";
import { fmtMoney, money } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  general: "General",
  sponsorship: "Sponsorship",
  partnership: "Partnership",
  guest: "Guest Application",
};

const URGENCY = [
  { label: "Overdue", border: "border-l-red-500", badge: "bg-red-500/20 text-red-400" },
  { label: "This Week", border: "border-l-brand-accent", badge: "bg-brand-accent/15 text-brand-accent" },
  { label: "When You Can", border: "border-l-white/20", badge: "bg-white/10 text-white/60" },
];

const buildFollowUps = ({ subs, pipeline, deals, courses, guests, deliverables, revenue }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = (d) => Math.round((new Date(`${d}T00:00:00`) - today) / 86400000);
  const byDays = (days) => (days < 0 ? 0 : days <= 7 ? 1 : 2);
  const out = [];

  subs.forEach((s) => {
    if (s.status !== "new" && s.status !== "contacted") return;
    const created = s.created_at ? new Date(s.created_at) : today;
    const age = Math.floor((today - created) / 86400000);
    const kind = TYPE_LABELS[s.type] || "Message";
    out.push({
      key: `sub-${s.id}`,
      source: "Submission",
      tab: "submissions",
      title: s.name || "Unnamed",
      detail:
        s.status === "new"
          ? `New ${kind.toLowerCase()} — awaiting first response${s.message ? `: "${s.message.slice(0, 80)}${s.message.length > 80 ? "…" : ""}"` : ""}`
          : `${kind} — conversation in progress, keep it warm`,
      date: (s.created_at || "").slice(0, 10) || null,
      urgency: s.status === "new" ? (age >= 2 ? 0 : 1) : 2,
      days: -age,
    });
  });

  pipeline.forEach((p) => {
    if (["Signed", "Lost"].includes(p.stage)) return;
    if (!p.next_step) {
      out.push({ key: `pipe-${p.id}`, source: "Sponsor Lead", tab: "pipeline", title: p.brand, detail: `${p.stage} — no next step scheduled`, urgency: 2, days: null });
      return;
    }
    const days = daysUntil(p.next_step);
    out.push({
      key: `pipe-${p.id}`,
      source: "Sponsor Lead",
      tab: "pipeline",
      title: p.brand,
      detail: `Next step due ${p.next_step}${p.contact ? ` · ${p.contact}` : ""}`,
      date: p.next_step,
      urgency: byDays(days),
      days,
    });
  });

  deals.forEach((d) => {
    if (d.status !== "Active") return;
    if (d.renewal_date) {
      const days = daysUntil(d.renewal_date);
      if (days <= 30)
        out.push({ key: `deal-renew-${d.id}`, source: "Deal", tab: "deals", title: d.sponsor, detail: `Renewal ${days < 0 ? "overdue since" : "due"} ${d.renewal_date}`, date: d.renewal_date, urgency: byDays(days), days });
    }
    if (d.contract_end) {
      const days = daysUntil(d.contract_end);
      if (days <= 30)
        out.push({ key: `deal-end-${d.id}`, source: "Deal", tab: "deals", title: d.sponsor, detail: `Contract ends ${d.contract_end} — line up renewal or wrap-up`, date: d.contract_end, urgency: byDays(days), days });
    }
  });

  courses.forEach((c) => {
    if (c.status === "Contacted")
      out.push({ key: `course-${c.id}`, source: "Course", tab: "courses", title: c.name, detail: "Contacted — nudge them to lock in a film date", urgency: 1, days: null });
    else if (c.status === "Want to Film")
      out.push({ key: `course-${c.id}`, source: "Course", tab: "courses", title: c.name, detail: "On the wishlist — reach out to book", urgency: 2, days: null });
  });

  guests.forEach((g) => {
    if (g.status === "Researching")
      out.push({ key: `guest-${g.id}`, source: "Guest", tab: "guest_research", title: g.guest, detail: "Research in progress — finish the sheet before filming", urgency: 2, days: null });
    else if (g.status === "Ready")
      out.push({ key: `guest-${g.id}`, source: "Guest", tab: "guest_research", title: g.guest, detail: "Research ready — schedule the shoot", urgency: 1, days: null });
  });

  deliverables.forEach((d) => {
    if (d.status === "Delivered") return;
    if (!d.due_date) {
      out.push({ key: `deliv-${d.id}`, source: "Deliverable", tab: "deliverables", title: d.deliverable || d.sponsor, detail: `Owed to ${d.sponsor} — no due date set`, urgency: 2, days: null });
      return;
    }
    const days = daysUntil(d.due_date);
    out.push({
      key: `deliv-${d.id}`,
      source: "Deliverable",
      tab: "deliverables",
      title: d.deliverable || d.sponsor,
      detail: `${d.status === "Owed" ? "Owed" : "In progress"} for ${d.sponsor} — ${d.type || "deliverable"} due ${d.due_date}`,
      date: d.due_date,
      urgency: byDays(days),
      days,
    });
  });

  revenue.forEach((r) => {
    if (r.status !== "Outstanding") return;
    const days = r.date ? daysUntil(r.date) : null;
    out.push({
      key: `rev-${r.id}`,
      source: "Payment",
      tab: "revenue",
      title: r.label || "Outstanding payment",
      detail: `${fmtMoney(money(r.amount))} outstanding${r.source ? ` from ${r.source}` : ""}${r.date ? ` — expected ${r.date}` : ""}`,
      date: r.date || null,
      urgency: days === null ? 1 : byDays(days),
      days,
    });
  });

  return out.sort((a, b) => a.urgency - b.urgency || (a.days ?? 9999) - (b.days ?? 9999));
};

export default function FollowUpCenter({ token, onAuthFail, onJump }) {
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [subs, pipeline, deals, courses, guests, deliverables, revenue] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers }),
        axios.get(`${API}/admin/board/pipeline`, { headers }),
        axios.get(`${API}/admin/board/deals`, { headers }),
        axios.get(`${API}/admin/board/courses`, { headers }),
        axios.get(`${API}/admin/board/guest_research`, { headers }),
        axios.get(`${API}/admin/board/deliverables`, { headers }),
        axios.get(`${API}/admin/board/revenue`, { headers }),
      ]);
      setItems(
        buildFollowUps({
          subs: subs.data.submissions,
          pipeline: pipeline.data.items,
          deals: deals.data.items,
          courses: courses.data.items,
          guests: guests.data.items,
          deliverables: deliverables.data.items,
          revenue: revenue.data.items,
        })
      );
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else setItems([]);
    } finally {
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = (items || []).reduce(
    (acc, it) => {
      acc[it.urgency] += 1;
      return acc;
    },
    [0, 0, 0]
  );

  return (
    <div className="mt-8" data-testid="followup-center">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="followup-count">
            Follow-Up Center · {items === null ? "…" : `${items.length} open item${items.length === 1 ? "" : "s"}`}
          </p>
          {items && items.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="followup-summary">
              {URGENCY.map((u, i) => (
                <span key={u.label} className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${u.badge}`}>
                  {u.label}: {counts[i]}
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={load} data-testid="followup-refresh" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-brand-accent hover:text-white">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {items === null ? (
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-white/40">Pulling follow-ups from every board…</p>
      ) : items.length === 0 ? (
        <div className="mt-6 grid place-items-center gap-4 border border-dashed border-white/15 py-20 text-center" data-testid="followup-empty">
          <CheckCircle2 className="h-8 w-8 text-brand-accent" />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">All caught up — nothing needs a follow-up</p>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-3" data-testid="followup-list">
          {items.map((it) => {
            const u = URGENCY[it.urgency];
            return (
              <div key={it.key} data-testid={`followup-item-${it.key}`} className={`flex flex-wrap items-center justify-between gap-4 border border-white/10 border-l-2 ${u.border} bg-ink-2 p-4`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${u.badge}`}>{u.label}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/35">{it.source}</span>
                  </div>
                  <p className="mt-2 font-display text-base font-extrabold uppercase tracking-tight text-white">{it.title}</p>
                  <p className="mt-1 font-mono text-[11px] leading-relaxed text-muted">{it.detail}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {it.date && <span className="font-mono text-[10px] text-white/40">{it.date}</span>}
                  <button onClick={() => onJump(it.tab)} data-testid={`followup-open-${it.key}`} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent">
                    Open
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
